import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, doc, setDoc, deleteDoc, where, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { db, auth, getCompanyId, secondaryAuth } from '../firebase';
import Navbar from '../components/Navbar';

export default function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [flexibleClockIn, setFlexibleClockIn] = useState(false);

  useEffect(() => {
    const setup = async () => {
      const companyId = await getCompanyId();
      const unsub = onSnapshot(query(collection(db, 'users'), where('companyId', '==', companyId)), (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setEmployees(data.filter(u => u.role === 'employee'));
        setLoading(false);
      });
      return () => unsub();
    };
    setup();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError('');
    try {
      const companyId = await getCompanyId();
      const settingsDoc = await getDoc(doc(db, 'settings', companyId));
      if (settingsDoc.exists() && settingsDoc.data().allowedDomain) {
        const allowedDomain = settingsDoc.data().allowedDomain;
        if (!email.endsWith(allowedDomain)) {
          setError(`Only ${allowedDomain} emails are allowed`);
          setAdding(false);
          return;
        }
      }
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      await secondaryAuth.signOut();;
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        name,
        email,
        role: 'employee',
        companyId,
        flexibleClockIn,
        createdAt: new Date(),
      });
      setName('');
      setEmail('');
      setPassword('');
      setFlexibleClockIn(false);
      setShowAdd(false);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('That email is already in use');
      } else {
        setError(err.message);
      }
    }
    setAdding(false);
  };
  const handleDelete = async (emp) => {
    if (!window.confirm(`Remove ${emp.name}? This cannot be undone.`)) return;
    try {
      const companyId = await getCompanyId();

      const shiftsSnap = await getDocs(query(collection(db, 'shifts'), where('companyId', '==', companyId)));
      for (const shiftDoc of shiftsSnap.docs) {
        const shift = shiftDoc.data();
        if (shift.employeesByDay) {
          const updated = { ...shift.employeesByDay };
          let changed = false;
          for (const day of Object.keys(updated)) {
            if (updated[day].includes(emp.id)) {
              updated[day] = updated[day].filter(id => id !== emp.id);
              changed = true;
            }
          }
          if (changed) await updateDoc(doc(db, 'shifts', shiftDoc.id), { employeesByDay: updated });
        }
        if (shift.employeeIds?.includes(emp.id)) {
          await updateDoc(doc(db, 'shifts', shiftDoc.id), {
            employeeIds: shift.employeeIds.filter(id => id !== emp.id)
          });
        }
      }

      const entriesSnap = await getDocs(query(collection(db, 'timeEntries'), where('companyId', '==', companyId), where('userId', '==', emp.id)));
      for (const entryDoc of entriesSnap.docs) {
        await updateDoc(doc(db, 'timeEntries', entryDoc.id), { employeeDeleted: true });
      }

      await deleteDoc(doc(db, 'users', emp.id));
      alert(`${emp.name} has been removed.`);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Employees</h1>
          <button style={styles.addButton} onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? 'Cancel' : '+ Add Employee'}
          </button>
        </div>

        {showAdd && (
          <div style={styles.addCard}>
            <h3 style={styles.addTitle}>New Employee</h3>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleAdd} style={styles.form}>
              <input
                style={styles.input}
                placeholder="Full Name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
              <input
                style={styles.input}
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <input
                style={styles.input}
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <div
                style={styles.checkboxRow}
                onClick={() => setFlexibleClockIn(!flexibleClockIn)}
              >
                <div style={{
                  ...styles.checkbox,
                  ...(flexibleClockIn ? styles.checkboxActive : {})
                }}>
                  {flexibleClockIn && '✓'}
                </div>
                <div>
                  <div style={styles.checkboxTitle}>Clock in anytime?</div>
                  <div style={styles.checkboxSubtitle}>Employee can clock in without an active shift</div>
                </div>
              </div>
              <button style={styles.submitButton} type="submit" disabled={adding}>
                {adding ? 'Adding...' : 'Add Employee'}
              </button>
            </form>
          </div>
        )}

        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Name', 'Email', 'Added', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>
                    No employees yet. Add one!
                  </td>
                </tr>
              ) : employees.map(emp => (
                <tr
                  key={emp.id}
                  style={{ ...styles.tr, cursor: 'pointer' }}
                  onClick={() => navigate(`/employees/${emp.id}`)}
                >
                  <td style={styles.td}>{emp.name}</td>
                  <td style={styles.td}>{emp.email}</td>
                  <td style={styles.td}>
                    {emp.createdAt ? new Date(emp.createdAt.seconds * 1000).toLocaleDateString() : '--'}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.rowActions}>
                      <button
                        style={styles.scheduleBtn}
                        onClick={(e) => { e.stopPropagation(); navigate(`/employees/${emp.id}`); }}
                      >
                        Schedule
                      </button>
                      <button
                        style={styles.deleteBtn}
                        onClick={(e) => { e.stopPropagation(); handleDelete(emp); }}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0f0f0f',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#2a2a2a',
    borderRadius: 10,
    padding: '12px 16px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 5,
    border: '2px solid #555',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 13,
    fontWeight: 'bold',
    flexShrink: 0,
  },
  checkboxActive: {
    backgroundColor: '#4f46e5',
    border: '2px solid #4f46e5',
  },
  checkboxTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  checkboxSubtitle: {
    color: '#999',
    fontSize: 12,
    marginTop: 2,
  },
  container: {
    padding: '32px 40px',
    maxWidth: 1200,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    margin: 0,
  },
  addButton: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  addCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  addTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 16,
  },
  error: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  form: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    minWidth: 200,
    backgroundColor: '#2a2a2a',
    border: 'none',
    borderRadius: 10,
    padding: '12px 16px',
    color: '#fff',
    fontSize: 15,
    outline: 'none',
  },
  submitButton: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    overflow: 'hidden',
  },
  th: {
    padding: '14px 20px',
    textAlign: 'left',
    color: '#999',
    fontSize: 13,
    fontWeight: '600',
    borderBottom: '1px solid #2a2a2a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tr: {
    borderBottom: '1px solid #2a2a2a',
  },
  td: {
    padding: '14px 20px',
    color: '#fff',
    fontSize: 14,
  },
  empty: {
    color: '#999',
    textAlign: 'center',
    padding: 40,
  },
  rowActions: {
    display: 'flex',
    gap: 8,
  },
  scheduleBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 'bold',
  },
  deleteBtn: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 'bold',
  },
};